import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  BookOpen, MessageCircle, Baby, User, Building2, Languages,
  Sparkles, HeartHandshake, Target, Users, Layers, Smile, Quote, Star,
} from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Reveal, Kicker } from "@/components/site/Reveal";
import { BtnPrimary, BtnSecondary, BtnAccent } from "@/components/site/Buttons";
import { BUSINESS } from "@/i18n/content";

const IMG = {
  hero: "https://images.pexels.com/photos/37827726/pexels-photo-37827726.jpeg?auto=compress&cs=tinysrgb&w=1200",
  founder: "https://customer-assets-eiarnc6j.emergentagent.net/job_alma-lengua-preview/artifacts/l0t2ljgb_IMG_20260513_162825459~3%20%281%29.jpg",
  workplace: "https://images.unsplash.com/photo-1758691737182-d42aefd6dee8?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
  community: "https://customer-assets-eiarnc6j.emergentagent.net/job_alma-lengua-preview/artifacts/nuk5bkzd_FB_IMG_1778150222725.webp",
};

const HERO_VIDEO = "https://customer-assets-eiarnc6j.emergentagent.net/job_alma-lengua-preview/artifacts/0ljaipfz_VID_20260515_144356051.mp4";

const serviceIcons = [BookOpen, MessageCircle, Baby, User, Building2, Languages];
const whyIcons = [MessageCircle, Sparkles, Target, HeartHandshake, Layers, Smile];

const HeroLine = ({ children, delay }) => (
  <span className="line-mask">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Home() {
  const { tr } = useLang();
  const h = tr.home;
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 80]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative overflow-hidden">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 pt-14 pb-20 lg:pt-20 lg:pb-28 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
              <Kicker>Oxford, Pennsylvania</Kicker>
            </motion.div>
            <h1 className="font-heading font-extrabold text-navy text-[2.6rem] sm:text-6xl lg:text-[4.4rem] leading-[1.02] tracking-tight">
              {h.heroLines.map((line, i) => (
                <HeroLine key={i} delay={0.15 + i * 0.12}>{line}</HeroLine>
              ))}
            </h1>
            <motion.p
              className="mt-7 text-lg md:text-xl text-ink/70 max-w-xl leading-relaxed"
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.7 }}
            >
              {h.heroSub}
            </motion.p>
            <motion.div className="mt-9 flex flex-wrap gap-4"
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.7 }}>
              <BtnPrimary to="/contact" testid="hero-contact-btn">{h.ctaPrimary}</BtnPrimary>
              <BtnSecondary to="/services" testid="hero-services-btn">{h.ctaSecondary}</BtnSecondary>
            </motion.div>
          </div>

          <div className="lg:col-span-5">
            <motion.div
              className="relative"
              initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.4, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="arch overflow-hidden shadow-[0_30px_80px_rgba(7,20,63,0.18)] aspect-[4/5]">
                <motion.video
                  src={HERO_VIDEO}
                  autoPlay muted loop playsInline preload="auto"
                  poster="https://images.pexels.com/photos/37827726/pexels-photo-37827726.jpeg?auto=compress&cs=tinysrgb&w=1200"
                  aria-label="Alma y Lengua learning center video"
                  style={{ y: imgY, scale: imgScale }}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-5 -left-5 bg-gold text-navy rounded-2xl px-5 py-4 shadow-xl max-w-[200px]">
                <p className="font-heading font-extrabold text-sm leading-snug">English &amp; Espa&#241;ol</p>
                <p className="text-xs font-semibold text-navy/70">for real-life communication</p>
              </div>
            </motion.div>
          </div>
        </div>

        {/* trust bar */}
        <div className="mx-auto max-w-7xl px-5 lg:px-8 pb-4">
          <Reveal className="rounded-3xl bg-navy text-white grain p-8 md:p-10">
            <p className="relative text-center text-lg md:text-xl font-medium max-w-4xl mx-auto leading-relaxed">
              {h.trust}
            </p>
          </Reveal>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="py-6 border-y border-navy/10 bg-aqua/40">
        <Marquee speed={38} gradient={false} className="overflow-hidden">
          <span className="font-accent italic text-2xl md:text-3xl text-navy/80 mr-8">{h.marquee}</span>
          <span className="font-accent italic text-2xl md:text-3xl text-navy/80 mr-8">{h.marquee}</span>
        </Marquee>
      </div>

      {/* SERVICES BENTO */}
      <section className="py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal className="max-w-2xl mb-14">
            <Kicker>{h.servicesKicker}</Kicker>
            <h2 className="font-heading font-extrabold text-navy text-4xl md:text-5xl tracking-tight">{h.servicesTitle}</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {h.services.map((s, i) => {
              const Icon = serviceIcons[i];
              return (
                <Reveal key={i} delay={i * 0.06}>
                  <div data-testid={`service-card-${i}`}
                    className="group h-full bg-white rounded-3xl p-8 hover:shadow-[0_8px_40px_rgba(7,20,63,0.1)] hover:-translate-y-2 transition-transform transition-shadow duration-300 border border-navy/5">
                    <div className="w-14 h-14 rounded-2xl bg-aqua grid place-items-center text-brand mb-6 group-hover:bg-navy group-hover:text-gold transition-colors">
                      <Icon size={26} />
                    </div>
                    <h3 className="font-heading font-bold text-xl text-navy mb-3">{s.title}</h3>
                    <p className="text-ink/65 leading-relaxed mb-6">{s.desc}</p>
                    <BtnSecondary to="/services" testid={`service-learn-${i}`} className="!px-5 !py-2.5 text-sm">
                      {tr.common.learnMore}
                    </BtnSecondary>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY / MANIFESTO */}
      <section className="bg-navy text-white grain py-24 lg:py-32">
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal className="max-w-3xl mb-16">
            <Kicker dark>{h.whyKicker}</Kicker>
            <h2 className="font-heading font-extrabold text-4xl md:text-5xl tracking-tight leading-tight">{h.whyTitle}</h2>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12">
            {h.why.map((w, i) => {
              const Icon = whyIcons[i];
              return (
                <Reveal key={i} delay={i * 0.05}>
                  <div data-testid={`why-item-${i}`} className="border-t border-white/15 pt-6">
                    <div className="flex items-center justify-between mb-4">
                      <span className="font-heading font-extrabold text-5xl text-white/15">0{i + 1}</span>
                      <Icon size={24} className="text-gold" />
                    </div>
                    <h3 className="font-heading font-bold text-xl mb-3">{w.title}</h3>
                    <p className="text-white/60 leading-relaxed">{w.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ABOUT PREVIEW */}
      <section className="py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="relative max-w-md">
              <div className="arch overflow-hidden shadow-[0_30px_80px_rgba(7,20,63,0.15)] aspect-[4/5] bg-aqua">
                <img src={IMG.founder} alt="La Profesora Alma, founder of Alma y Lengua Bilingual Learning Center" style={{ objectPosition: "center 15%" }} className="w-full h-full object-cover" />
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <Kicker>{h.aboutKicker}</Kicker>
            <h2 className="font-heading font-extrabold text-navy text-4xl md:text-5xl tracking-tight mb-6">{h.aboutTitle}</h2>
            <p className="text-lg text-ink/70 leading-relaxed mb-8">{h.aboutBody}</p>
            <BtnPrimary to="/about" testid="about-preview-btn">{h.aboutCta}</BtnPrimary>
          </Reveal>
        </div>
      </section>

      {/* WORKPLACE */}
      <section className="py-24 lg:py-32 bg-aqua/50">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <Kicker>{h.workKicker}</Kicker>
            <h2 className="font-heading font-extrabold text-navy text-4xl md:text-5xl tracking-tight mb-6">{h.workTitle}</h2>
            <p className="text-lg text-ink/70 leading-relaxed mb-7">{h.workBody}</p>
            <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-3 mb-8">
              {h.workExamples.map((e, i) => (
                <li key={i} className="flex items-center gap-3 text-ink/80">
                  <span className="w-2 h-2 rounded-full bg-gold shrink-0" /> {e}
                </li>
              ))}
            </ul>
            <BtnAccent to="/contact" testid="workplace-btn">{h.workCta}</BtnAccent>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="relative rounded-[2rem] overflow-hidden shadow-[0_30px_80px_rgba(7,20,63,0.15)] aspect-[4/3]">
              <img src={IMG.workplace} alt="Professionals in a workplace language training session" className="w-full h-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* PATHWAY */}
      <section className="py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal className="max-w-2xl mb-16">
            <Kicker>{h.pathKicker}</Kicker>
            <h2 className="font-heading font-extrabold text-navy text-4xl md:text-5xl tracking-tight mb-5">{h.pathTitle}</h2>
            <p className="text-lg text-ink/70 leading-relaxed">{h.pathIntro}</p>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {h.pathSteps.map((step, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div data-testid={`pathway-step-${i}`} className="relative bg-white rounded-3xl p-8 border border-navy/5 h-full">
                  <span className="font-heading font-extrabold text-6xl text-brand/15">{i + 1}</span>
                  <h3 className="font-heading font-bold text-lg text-navy mt-2">{step}</h3>
                  {i < 3 && <span className="hidden lg:block absolute top-1/2 -right-3 w-6 h-0.5 bg-gold/60" />}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* COMMUNITY */}
      <section className="py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal className="rounded-[2.5rem] bg-navy text-white grain p-10 md:p-16 relative overflow-hidden">
            <div className="relative grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <Kicker dark>{h.commKicker}</Kicker>
                <h2 className="font-heading font-extrabold text-3xl md:text-5xl tracking-tight mb-6">{h.commTitle}</h2>
                <p className="text-white/70 text-lg leading-relaxed mb-8">{h.commBody}</p>
                <BtnAccent to="/contact" testid="community-btn">{h.commCta}</BtnAccent>
              </div>
              <div className="rounded-2xl overflow-hidden aspect-[4/3] shadow-2xl">
                <img src={IMG.community} alt="Children and families at a Bilingual Exploradores community program" className="w-full h-full object-cover" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 lg:py-32 bg-aqua/40">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal className="max-w-2xl mb-14">
            <Kicker>{h.testiKicker}</Kicker>
            <h2 className="font-heading font-extrabold text-navy text-4xl md:text-5xl tracking-tight">{h.testiTitle}</h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {h.testimonials.map((t, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div data-testid={`testimonial-${i}`} className="bg-white rounded-3xl p-8 border border-navy/5 h-full flex flex-col shadow-[0_8px_30px_rgba(7,20,63,0.05)]">
                  <div className="flex gap-1 mb-5 text-gold">
                    {[0, 1, 2, 3, 4].map((s) => <Star key={s} size={18} fill="currentColor" strokeWidth={0} />)}
                  </div>
                  <Quote size={28} className="text-aqua mb-3" fill="currentColor" strokeWidth={0} />
                  <p className="text-ink/75 leading-relaxed flex-1">{t.quote}</p>
                  <div className="mt-6 flex items-center gap-3 pt-5 border-t border-navy/5">
                    <span className="w-10 h-10 rounded-full bg-navy text-white grid place-items-center font-heading font-bold text-sm">
                      {t.name.split(" ").map((w) => w[0]).join("").slice(0, 2)}
                    </span>
                    <span className="font-heading font-bold text-navy">{t.name}</span>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1} className="mt-12 text-center">
            <a href={BUSINESS.googleReviews} target="_blank" rel="noopener noreferrer" data-testid="google-reviews-btn"
              className="inline-flex items-center justify-center gap-2 bg-white text-navy border border-navy/10 rounded-full px-8 py-4 font-semibold hover:bg-white hover:shadow-lg transition-shadow">
              <Star size={18} className="text-gold" fill="currentColor" strokeWidth={0} /> {h.testiCta}
            </a>
          </Reveal>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 lg:py-32">
        <div className="mx-auto max-w-5xl px-5 lg:px-8 text-center">
          <Reveal>
            <h2 className="font-heading font-extrabold text-navy text-4xl md:text-6xl tracking-tight mb-6">
              {h.finalTitle}
            </h2>
            <p className="text-lg md:text-xl text-ink/70 leading-relaxed max-w-2xl mx-auto mb-10">{h.finalBody}</p>
            <div className="flex flex-wrap gap-4 justify-center">
              <BtnPrimary to="/contact" testid="final-contact-btn">{h.finalPrimary}</BtnPrimary>
              <BtnSecondary to="/services" testid="final-services-btn">{h.finalSecondary}</BtnSecondary>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
